import { KeyIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import type { AccountSecurity } from '@/api'
import { SettingsRow, SettingsRowSkeleton } from '@/components/SettingsRow'
import { useAccountAppContext } from '@/app/AccountAppContext'
import type { LocalMethodProps } from '@/features/security/local/LocalSecuritySection'

import { PasswordChangeDialog } from './PasswordChangeDialog'
import { usePasswordChange } from './usePasswordChange'

type PasswordMethodProps = LocalMethodProps & {
    password: AccountSecurity['password'] | undefined
}

export const PasswordMethod = ({ password, disabled, runAction }: PasswordMethodProps) => {
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [checkingAuthentication, setCheckingAuthentication] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const { ensureFreshAuthentication } = useAccountAppContext()

    const returnUrl = new URL(window.location.href)
    returnUrl.searchParams.set('action', 'change-password')
    const returnTo = returnUrl.toString()

    const changePassword = usePasswordChange(returnTo)

    const handleOpen = useCallback(async () => {
        await runAction(async () => {
            setCheckingAuthentication(true)

            try {
                if (await ensureFreshAuthentication(returnTo)) {
                    setIsOpen(true)
                }
            } finally {
                setCheckingAuthentication(false)
            }
        })
    }, [ensureFreshAuthentication, returnTo, runAction])

    useEffect(() => {
        if (disabled || !password?.enabled || searchParams.get('action') !== 'change-password') {
            return
        }

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('action')
        setSearchParams(nextParams, { replace: true })
        void handleOpen()
    }, [disabled, handleOpen, password?.enabled, searchParams, setSearchParams])

    const handleSubmit = async (newPassword: string) => {
        await runAction(async () => {
            await changePassword.mutateAsync({ newPassword })
            setIsOpen(false)
        })
    }

    if (!password) {
        return <SettingsRowSkeleton />
    }

    return (
        <>
            <SettingsRow
                icon={<KeyIcon className="h-5 w-5" aria-hidden="true" />}
                title={t('security.local.password.title')}
                description={
                    password.enabled
                        ? t('security.local.password.description')
                        : t('security.local.password.unavailable')
                }
                actionLabel={t('security.local.password.changeAction')}
                onAction={
                    password.enabled
                        ? () => {
                              void handleOpen()
                          }
                        : undefined
                }
                disabled={disabled}
                pending={checkingAuthentication || changePassword.isPending}
            />
            {isOpen && (
                <PasswordChangeDialog
                    minLength={password.minLength}
                    strength={password.strength}
                    onClose={() => setIsOpen(false)}
                    onSubmit={handleSubmit}
                />
            )}
        </>
    )
}
